import React, { useCallback, useEffect, useState } from "react";
import {
    Box,
    Button,
    Chip,
    Link,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from "@mui/material";
import toast from "react-hot-toast";
import axiosInstant from "../../../api";
import {
    adminRmnDiscoveredProductsEndpoint,
    adminRmnDiscoveredBrandsEndpoint,
    adminRmnAutoMatchEndpoint,
    adminRmnIgnoreProductEndpoint,
    adminRmnIgnoreBrandEndpoint,
} from "../../../api/endpoints";
import {
    RmnDiscoveredBrand,
    RmnDiscoveredProduct,
    RmnMatchStatus,
} from "../../../types/rmn";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { DotLottleLoader, LoadMoreBtn } from "../../../components/Loader";
import { PromoteMatchModal } from "./PromoteMatchModal";

const PAGE_SIZE = 25;

type Mode = "products" | "brands";

const STATUS_FILTERS: { value: RmnMatchStatus; label: string }[] = [
    { value: "pending", label: "Pending" },
    { value: "auto_matched", label: "Auto-matched" },
    { value: "promoted", label: "Imported" },
    { value: "ignored", label: "Ignored" },
];

const STATUS_CHIP: Record<RmnMatchStatus, { label: string; bg: string; fg: string }> = {
    pending: { label: "Pending", bg: "var(--surface-2)", fg: "var(--text-2)" },
    auto_matched: { label: "Auto-matched", bg: "rgba(36,116,221,.14)", fg: "#2474DD" },
    promoted: { label: "Imported", bg: "var(--up-bg)", fg: "var(--up)" },
    ignored: { label: "Ignored", bg: "var(--surface-2)", fg: "var(--muted)" },
};

const statusChip = (status: RmnMatchStatus) => {
    const c = STATUS_CHIP[status] || STATUS_CHIP.pending;
    return (
        <Chip
            label={c.label}
            size="small"
            sx={{ bgcolor: c.bg, color: c.fg, fontWeight: 600 }}
        />
    );
};

// Promote/Ignore only make sense for rows still awaiting review.
const isActionable = (status: RmnMatchStatus) =>
    status === "pending" || status === "auto_matched";

interface DiscoveredReviewPanelProps {
    brandId: number;
}

type PromoteTarget =
    | { mode: "product"; record: RmnDiscoveredProduct }
    | { mode: "brand"; record: RmnDiscoveredBrand };

// Internal-admin review of crawl-discovered products/brands: filter by match
// status, auto-match, then promote (link or create canonical) or ignore.
export function DiscoveredReviewPanel({ brandId }: DiscoveredReviewPanelProps) {
    const [mode, setMode] = useState<Mode>("products");
    const [status, setStatus] = useState<RmnMatchStatus>("pending");
    const [searchInput, setSearchInput] = useState("");
    const [search, setSearch] = useState("");
    const [rows, setRows] = useState<Array<RmnDiscoveredProduct | RmnDiscoveredBrand>>([]);
    const [count, setCount] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [busy, setBusy] = useState(false);
    const [promoteTarget, setPromoteTarget] = useState<PromoteTarget | null>(null);
    const [ignoreTarget, setIgnoreTarget] = useState<
        RmnDiscoveredProduct | RmnDiscoveredBrand | null
    >(null);

    // Debounce the search box into the fetch param.
    useEffect(() => {
        const timer = window.setTimeout(() => setSearch(searchInput.trim()), 300);
        return () => window.clearTimeout(timer);
    }, [searchInput]);

    const fetchRows = useCallback(
        async (pageNum: number, append: boolean) => {
            setLoading(true);
            try {
                const endpoint =
                    mode === "products"
                        ? adminRmnDiscoveredProductsEndpoint(brandId)
                        : adminRmnDiscoveredBrandsEndpoint(brandId);
                const res = await axiosInstant.get(endpoint, {
                    params: {
                        match_status: status,
                        search: search || undefined,
                        page: pageNum,
                        limit: PAGE_SIZE,
                    },
                });
                const newRows = (res.data && res.data.rows) || [];
                setCount((res.data && res.data.count) || 0);
                setRows((prev) => (append ? [...prev, ...newRows] : newRows));
            } catch (err) {
                toast.error("Failed to load discovered records");
            } finally {
                setLoading(false);
            }
        },
        [brandId, mode, status, search]
    );

    useEffect(() => {
        setPage(1);
        setRows([]);
        fetchRows(1, false);
    }, [fetchRows]);

    const refetch = () => {
        setPage(1);
        fetchRows(1, false);
    };

    const loadMore = () => {
        const next = page + 1;
        setPage(next);
        fetchRows(next, true);
    };

    const runAutoMatch = async () => {
        setBusy(true);
        try {
            const res = await axiosInstant.post(adminRmnAutoMatchEndpoint(brandId));
            const scanned = (res.data && res.data.scanned) || 0;
            const matched = (res.data && res.data.matched) || 0;
            toast.success(`Matched ${matched} of ${scanned} scanned`);
            refetch();
        } catch (err) {
            toast.error("Auto-match failed");
        } finally {
            setBusy(false);
        }
    };

    const confirmIgnore = async () => {
        if (!ignoreTarget) return;
        setBusy(true);
        try {
            const endpoint =
                mode === "products"
                    ? adminRmnIgnoreProductEndpoint(ignoreTarget.id)
                    : adminRmnIgnoreBrandEndpoint(ignoreTarget.id);
            await axiosInstant.post(endpoint);
            toast.success("Ignored");
            setIgnoreTarget(null);
            refetch();
        } catch (err) {
            toast.error("Failed to ignore");
        } finally {
            setBusy(false);
        }
    };

    const hasMore = rows.length < count;

    return (
        <Box className="flex flex-col gap-4">
            <Box className="flex flex-wrap items-center justify-between gap-3">
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={mode}
                        onChange={(_, value: Mode | null) => {
                            if (value) setMode(value);
                        }}
                    >
                        <ToggleButton value="products">Products</ToggleButton>
                        <ToggleButton value="brands">Brands</ToggleButton>
                    </ToggleButtonGroup>
                    <Stack direction="row" spacing={1}>
                        {STATUS_FILTERS.map((f) => (
                            <Chip
                                key={f.value}
                                label={f.label}
                                size="small"
                                clickable
                                color={status === f.value ? "primary" : "default"}
                                variant={status === f.value ? "filled" : "outlined"}
                                onClick={() => setStatus(f.value)}
                            />
                        ))}
                    </Stack>
                </Stack>
                <Stack direction="row" spacing={1.5} alignItems="center">
                    <TextField
                        size="small"
                        placeholder="Search"
                        value={searchInput}
                        onChange={(e) => setSearchInput(e.target.value)}
                        sx={{ minWidth: 220 }}
                    />
                    {mode === "products" && (
                        <Button
                            variant="outlined"
                            size="small"
                            disabled={busy}
                            onClick={runAutoMatch}
                        >
                            Auto-match
                        </Button>
                    )}
                </Stack>
            </Box>

            <Typography variant="caption" color="text.secondary">
                {count} {mode === "products" ? "products" : "brands"}
            </Typography>

            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        {mode === "products" ? (
                            <TableRow>
                                <TableCell />
                                <TableCell>Title</TableCell>
                                <TableCell>Price</TableCell>
                                <TableCell>Brand</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Matched</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        ) : (
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>URL</TableCell>
                                <TableCell>Status</TableCell>
                                <TableCell>Matched</TableCell>
                                <TableCell align="right">Actions</TableCell>
                            </TableRow>
                        )}
                    </TableHead>
                    <TableBody>
                        {mode === "products" &&
                            (rows as RmnDiscoveredProduct[]).map((p) => (
                                <TableRow key={p.id} hover>
                                    <TableCell sx={{ width: 56 }}>
                                        {p.image && (
                                            <Box
                                                component="img"
                                                src={p.image}
                                                alt=""
                                                sx={{
                                                    width: 40,
                                                    height: 40,
                                                    objectFit: "contain",
                                                    borderRadius: 0.5,
                                                }}
                                            />
                                        )}
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 320 }}>
                                        <Link
                                            href={p.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            underline="hover"
                                            color="inherit"
                                        >
                                            <Typography variant="body2" noWrap>
                                                {p.title || p.url}
                                            </Typography>
                                        </Link>
                                    </TableCell>
                                    <TableCell>{p.price || "—"}</TableCell>
                                    <TableCell>{p.brand_text || "—"}</TableCell>
                                    <TableCell>
                                        {p.rmn_category ? p.rmn_category.name : "—"}
                                    </TableCell>
                                    <TableCell>{statusChip(p.match_status)}</TableCell>
                                    <TableCell sx={{ maxWidth: 200 }}>
                                        <Typography variant="body2" noWrap>
                                            {p.matched_desire ? p.matched_desire.title : "—"}
                                        </Typography>
                                    </TableCell>
                                    <TableCell align="right">
                                        {isActionable(p.match_status) && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        setPromoteTarget({
                                                            mode: "product",
                                                            record: p,
                                                        })
                                                    }
                                                >
                                                    Import
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    disabled={busy}
                                                    onClick={() => setIgnoreTarget(p)}
                                                >
                                                    Ignore
                                                </Button>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        {mode === "brands" &&
                            (rows as RmnDiscoveredBrand[]).map((b) => (
                                <TableRow key={b.id} hover>
                                    <TableCell>
                                        <Box className="flex items-center gap-2">
                                            {b.logo && (
                                                <Box
                                                    component="img"
                                                    src={b.logo}
                                                    alt=""
                                                    sx={{
                                                        width: 28,
                                                        height: 28,
                                                        objectFit: "contain",
                                                        borderRadius: 0.5,
                                                    }}
                                                />
                                            )}
                                            <Typography variant="body2">{b.name}</Typography>
                                        </Box>
                                    </TableCell>
                                    <TableCell sx={{ maxWidth: 280 }}>
                                        {b.url ? (
                                            <Link
                                                href={b.url}
                                                target="_blank"
                                                rel="noreferrer"
                                                underline="hover"
                                            >
                                                <Typography variant="body2" noWrap>
                                                    {b.url}
                                                </Typography>
                                            </Link>
                                        ) : (
                                            "—"
                                        )}
                                    </TableCell>
                                    <TableCell>{statusChip(b.match_status)}</TableCell>
                                    <TableCell>
                                        {b.matched_brand ? b.matched_brand.name : "—"}
                                    </TableCell>
                                    <TableCell align="right">
                                        {isActionable(b.match_status) && (
                                            <Stack
                                                direction="row"
                                                spacing={1}
                                                justifyContent="flex-end"
                                            >
                                                <Button
                                                    size="small"
                                                    variant="outlined"
                                                    disabled={busy}
                                                    onClick={() =>
                                                        setPromoteTarget({
                                                            mode: "brand",
                                                            record: b,
                                                        })
                                                    }
                                                >
                                                    Import
                                                </Button>
                                                <Button
                                                    size="small"
                                                    color="inherit"
                                                    disabled={busy}
                                                    onClick={() => setIgnoreTarget(b)}
                                                >
                                                    Ignore
                                                </Button>
                                            </Stack>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        {!loading && rows.length === 0 && (
                            <TableRow>
                                <TableCell
                                    colSpan={mode === "products" ? 8 : 5}
                                    align="center"
                                >
                                    <Typography className="py-6" color="var(--muted)">
                                        No {mode === "products" ? "products" : "brands"} with
                                        this status.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {loading && rows.length === 0 && (
                <Box className="flex justify-center py-6">
                    <DotLottleLoader fill={false} />
                </Box>
            )}

            {rows.length > 0 && hasMore && (
                <LoadMoreBtn onClick={loadMore} loading={loading} customClasses="!my-2" />
            )}

            {promoteTarget && (
                <PromoteMatchModal
                    {...promoteTarget}
                    onClose={() => setPromoteTarget(null)}
                    onPromoted={() => {
                        setPromoteTarget(null);
                        refetch();
                    }}
                />
            )}

            <ConfirmationDialog
                open={!!ignoreTarget}
                onClose={() => setIgnoreTarget(null)}
                onConfirm={confirmIgnore}
                title={`Ignore this ${mode === "products" ? "product" : "brand"}?`}
                description="Ignored records are excluded from review and never imported into the catalog."
                confirmLabel="Ignore"
                disabled={busy}
            />
        </Box>
    );
}
